import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import JsonLd from '@/components/feature/JsonLd';
import SectionTitle from '@/components/feature/SectionTitle';
import SubscribeForm from '@/components/feature/SubscribeForm';
import FaqAccordion from '@/pages/about/components/FaqAccordion';
import { useSeo } from '@/hooks/useSeo';
import { brand, faqs, principles, stats, timelineMilestones, testimonials } from '@/mocks/site';

export default function About() {
  useSeo({
    title: '关于我们 - 揭秘局团队与内容原则 | 揭秘局',
    description:
      '揭秘局专注独家揭秘深度内幕与线报图解，介绍我们的网站定位、内容原则、发展历程与联系方式，以及读者最关心的常见问题解答。',
    keywords: '关于揭秘局,独家揭秘深度内幕,内容原则,线索核对,联系方式',
    path: '/about',
  });

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${brand.name}`,
    description: brand.intro,
    inLanguage: 'zh-CN',
    url: `${window.location.origin}/about`,
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brand.name,
    url: window.location.origin,
    email: brand.email,
    foundingDate: brand.founded,
    slogan: brand.slogan,
    description: brand.intro,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: window.location.origin },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${window.location.origin}/about` },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100 border-b border-background-200/70">
          <nav aria-label="面包屑" className="text-xs text-foreground-500">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
              <li className="text-foreground-800">关于我们</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black text-foreground-950 max-w-3xl">
            关于揭秘局：我们只想把线索讲清楚
          </h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
            {brand.intro}
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {stats.map((item) => (
              <div key={item.label} className="rounded-lg bg-background-50 border border-background-200/80 p-4">
                <div className="font-heading text-xl md:text-2xl font-black text-primary-600">{item.value}</div>
                <div className="mt-1 text-xs text-foreground-500">{item.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionTitle eyebrow="网站定位" title="一个克制的内幕整理者" />
              <div className="mt-6 space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  揭秘局不是一个追热度的账号，更像一个长期运行的档案室。我们关心的是：这件事从哪一步开始，中间有多少参与者，之后又发生了什么。
                </p>
                <p>
                  我们的读者里有内容从业者、品牌舆情岗，也有单纯吃瓜的普通人。他们的共同需求很朴素——不想在被删帖和截图之间反复跳转，只想找一条能顺着看下去的线。
                </p>
                <p>
                  所以我们把每篇稿子拆成三部分：正文讲过程，图解讲关系，时间线讲后续。三部分互相引用，任何一段都能独立看懂。
                </p>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-lg overflow-hidden border border-background-200/80">
                <div className="w-full h-64 md:h-80">
                  <img
                    src="https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20archive%20drawers%20and%20connected%20timeline%20cards%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious%20composition&width=1200&height=800&seq=jiemi-about-mission&orientation=landscape"
                    alt="揭秘局内容整理工作示意"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-5">
                  <i className="ri-map-pin-2-line text-xl text-secondary-700" />
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">内容方向</h3>
                  <p className="mt-2 text-xs text-foreground-600 leading-relaxed">
                    娱乐、商业、科技、财经、社会与文娱舆情六个长期跟踪方向。
                  </p>
                </div>
                <div className="rounded-lg bg-accent-50 border border-accent-200 p-5">
                  <i className="ri-shield-check-line text-xl text-accent-700" />
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">核对机制</h3>
                  <p className="mt-2 text-xs text-foreground-600 leading-relaxed">
                    单一来源标注为待核实，关键数字需多方交叉后才会写入正文。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <SectionTitle
            eyebrow="内容原则"
            title="四条我们不会让步的规则"
            desc="这些原则决定了我们写什么、怎么写，以及哪些内容宁可不写。"
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {principles.map((item, index) => (
              <div key={item.title} className="flex gap-4 rounded-lg bg-background-50 border border-background-200/80 p-6">
                <span className="shrink-0 w-10 h-10 rounded-md bg-primary-100 text-primary-700 font-heading font-black flex items-center justify-center">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16">
          <SectionTitle eyebrow="发展理念" title="从一张便签走到现在" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-6">
            {timelineMilestones.map((item) => (
              <div key={item.year} className="relative pt-6 border-t-2 border-background-200">
                <span className="absolute -top-[7px] left-0 w-3 h-3 rounded-full bg-primary-500" />
                <div className="font-heading text-lg font-black text-primary-600">{item.year}</div>
                <h3 className="mt-2 font-heading text-sm font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.slice(0, 3).map((item) => (
              <blockquote key={item.name} className="rounded-lg border border-background-200/80 bg-background-100 p-6">
                <p className="text-sm text-foreground-700 leading-relaxed">“{item.quote}”</p>
                <footer className="mt-4 text-xs text-foreground-500">
                  <strong className="text-foreground-900">{item.name}</strong> · {item.role}
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <SectionTitle
            eyebrow="常见问题"
            title="读者最常问的几件事"
            desc="还有别的疑问，可以直接发邮件给我们。"
          />
          <div className="mt-8">
            <FaqAccordion items={faqs} />
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionTitle eyebrow="联系我们" title="线报投稿与内容合作" />
              <div className="mt-6 space-y-4">
                <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-mail-line text-primary-600" />
                    读者来信与更正
                  </div>
                  <a href={`mailto:${brand.email}`} className="mt-2 block font-heading text-base font-bold text-foreground-950 hover:text-primary-600 transition-colors">
                    {brand.email}
                  </a>
                </div>
                <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-send-plane-line text-primary-600" />
                    线报投稿与合作
                  </div>
                  <a href={`mailto:${brand.cooperation}`} className="mt-2 block font-heading text-base font-bold text-foreground-950 hover:text-primary-600 transition-colors">
                    {brand.cooperation}
                  </a>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">{brand.statement}</p>
              </div>
            </div>
            <div className="lg:col-span-7">
              <SubscribeForm />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
      <JsonLd id="ld-about-page" data={aboutSchema} />
      <JsonLd id="ld-about-org" data={organizationSchema} />
      <JsonLd id="ld-about-faq" data={faqSchema} />
      <JsonLd id="ld-about-breadcrumb" data={breadcrumbSchema} />
    </div>
  );
}